import { AppBar, Toolbar, Box, Typography } from '@mui/material';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';

import { Maybe } from '@/types';
import { AnalyticsContext, App, BannerAlertMessagesCollection, NavigationLink, Page } from '@/types/generated';
import ImageWrapper from '@/components/ImageWrapper';
import TAGLink from '@/components/TAGLink';
import HeaderSearch from '@/components/Header/HeaderSearch';
import { clearChoiceConsts, DESKTOP_HEADER_ID, DESKTOP_HEADER_ID_WITH_NAV_MENU } from '@/constants';
import { useAppContext } from '@/context';
import { clickContext } from '@/utils';
import HeaderWithLocationNavDesktop from '@/components/HeaderWithLocationNav/HeaderDesktop';
import LanguageToggle from '@/components/LanguageToggle';
import { IGoogleMap } from '@/components/GoogleMap/GoogleMap';
import Banner from '@/components/Banner/Banner';

import HeaderOfficeLocation from '../HeaderOfficeLocation';
import useDisappearingHeader from '../hooks/useDissappearingHeader';
import useStickyNavHeader from '../hooks/useStickyNavHeader';
import NavigationBar from '../NavigationBar';
import { getWrapperStyles, topNavigationStyles } from '../NavigationBar/NavigationBar.styles';
import NavigationMenu from '../NavigationMenu/NavigationMenu';
import { officeNameStyles } from '../HeaderOfficeLocation/HeaderOfficeDetails/HeaderOfficeDetails.styles';
import HeaderSimpleFindOfficeLink from '../HeaderOfficeLocation/HeaderSimpleFindOfficeLink/HeaderSimpleFindOfficeLink';

import {
	headerStyles,
	logoStyles,
	navbarStyles,
	toolbarHeaderStyles,
	inlineNavigationStyles,
	getLogoContainerStyles,
	getNavMenuDesktopStyles,
	getPhoneIconStyles,
} from './HeaderDesktop.styles';

export interface IHeaderDesktop {
	headerData: App['header'];
	inlineNavigation?: boolean;
	logoUrl?: Maybe<string>;
	logoPosition?: 'left' | 'center';
	logoHref: string;
	navigationBar?: boolean;
	withOfficeDetails?: boolean;
	topLinks?: boolean;
	isDarkHeader: boolean;
	hidePrimaryActionButton: boolean;
	hideLocation: boolean;
	showHeaderNavMenu?: boolean;
	locationNavigationLinks?: Array<NavigationLink | Page> | undefined;
	phoneNumber?: Maybe<NavigationLink>;
	withFixedHeader?: boolean;
	showLanguageToggle?: boolean;
	useHeaderLocationCards?: boolean | null;
	googleMapsForLocationCards?: IGoogleMap;
	topAlertBannerMessages?: BannerAlertMessagesCollection;
	bottomAlertBannerMessages?: BannerAlertMessagesCollection;
	showTopAlertBannerMessages?: boolean;
	showBottomAlertBannerMessages?: boolean;
	variant?: Maybe<string>;
}

const getHiddenPosForHeaderScroll = (
	showHeaderNavMenu: boolean,
	topLinks: boolean,
	showTopAlertBannerMessages: boolean,
	showBottomAlertBannerMessages: boolean
) => {
	// need to add 48px if top and bottom alert banners are shown
	if (showHeaderNavMenu && topLinks) {
		return showTopAlertBannerMessages && showBottomAlertBannerMessages ? '-232px' : '-184px';
	}
	if (showHeaderNavMenu && !topLinks) {
		return showTopAlertBannerMessages && showBottomAlertBannerMessages ? '-184px' : '-136px';
	}
	return showTopAlertBannerMessages && showBottomAlertBannerMessages ? '-160px' : '-112px';
};

export default function HeaderDesktop({
	logoUrl,
	logoPosition = 'left',
	logoHref = '/',
	navigationBar = false,
	headerData,
	inlineNavigation = true,
	withOfficeDetails,
	topLinks = true,
	isDarkHeader,
	hidePrimaryActionButton,
	hideLocation,
	showHeaderNavMenu = false,
	locationNavigationLinks,
	phoneNumber,
	withFixedHeader = false,
	showLanguageToggle,
	useHeaderLocationCards = false,
	googleMapsForLocationCards,
	topAlertBannerMessages,
	bottomAlertBannerMessages,
	showTopAlertBannerMessages = false,
	showBottomAlertBannerMessages = false,
	variant = 'default',
}: Readonly<IHeaderDesktop>) {
	const headerElement = useDisappearingHeader({
		hiddenPos: getHiddenPosForHeaderScroll(
			showHeaderNavMenu,
			topLinks,
			showTopAlertBannerMessages,
			showBottomAlertBannerMessages
		),
		showingPos: '0',
		withFixedHeader,
	});
	const navElement = useStickyNavHeader({ scrollingPosition: '0' });
	const navMenuLinks = headerData?.navigationMenu?.headerMainNavLinksCollection;
	const { config } = useAppContext();
	const showHeaderWithLocationNav = config.featureFlags.showHeaderWithLocationNav;
	const useSimpleFindOfficeLink = config.featureFlags.useSimpleFindOfficeLink;
	const isClearChoice = config.name === 'ClearChoice';

	if (showHeaderWithLocationNav) {
		return <HeaderWithLocationNavDesktop headerData={headerData} logoHref={logoHref} logoUrl={logoUrl} />;
	}

	return showHeaderNavMenu ? (
		<AppBar
			ref={headerElement}
			sx={headerStyles(isDarkHeader, showHeaderNavMenu)}
			data-test-id="section_header_desktop_with_nav_menu"
			id={DESKTOP_HEADER_ID_WITH_NAV_MENU}
		>
			{showTopAlertBannerMessages && (
				<Banner
					dataTestId="header_desktop_top_alert_banner"
					variant={'Alert Banner'}
					alertMessagesCollection={topAlertBannerMessages}
				/>
			)}
			{topLinks && (
				<NavigationBar
					navigationItems={headerData?.topNavigationLinksCollection}
					navigationSx={topNavigationStyles}
					isUtilityNavigation
					isDarkHeader={isDarkHeader}
				/>
			)}
			<Toolbar disableGutters sx={toolbarHeaderStyles(showHeaderNavMenu)}>
				<Box display="flex" justifyContent="flex-start" width={'100%'} gap={1}>
					{headerData && !hideLocation && (
						<>
							{useSimpleFindOfficeLink ? (
								<HeaderSimpleFindOfficeLink
									locationBarFindOfficeUrl={headerData?.locationBarFindOfficeUrl}
									isDarkHeader={isDarkHeader}
									locationBarWithOutOfficeTitle={headerData.locationBarWithOutOfficeTitle ?? ''}
								/>
							) : (
								<HeaderOfficeLocation
									isLargeDevice
									headerData={headerData}
									withOfficeDetails={withOfficeDetails}
									isDarkHeader={isDarkHeader}
									locationNavigationLinks={locationNavigationLinks}
									useHeaderLocationCards={useHeaderLocationCards}
									googleMapsForLocationCards={googleMapsForLocationCards}
								/>
							)}
						</>
					)}
					{phoneNumber && (
						<Box
							component="section"
							display="flex"
							alignItems="center"
							data-test-id="phone_number_link_desktop"
							gap={0.5}
						>
							<CallOutlinedIcon
								sx={getPhoneIconStyles({ isDarkHeader })}
								data-test-id="phone_number_icon_desktop"
							/>
							{isClearChoice ? (
								// Render Unclickable phone number for ClearChoice
								<span>
									<Typography
										variant="buttonLabel"
										color={isDarkHeader ? 'text.light' : 'text.interactive'}
										sx={{ ...officeNameStyles, cursor: 'default', pointerEvents: 'none' }}
										className="InvocaNumber"
									>
										{phoneNumber.linkText}
									</Typography>
								</span>
							) : (
								<TAGLink
									noLinkStyle
									href={phoneNumber.externalUrl || '#'}
									analyticsContext={
										phoneNumber.analyticsContext ||
										({ clickPosition: 'header' } as AnalyticsContext)
									}
								>
									<Typography
										variant="buttonLabel"
										color={isDarkHeader ? 'text.light' : 'text.interactive'}
										sx={officeNameStyles}
									>
										{phoneNumber.linkText}
									</Typography>
								</TAGLink>
							)}
						</Box>
					)}
				</Box>
				<Box display="flex" justifyContent="flex-center" width={'100%'} flexShrink={'2'}>
					<TAGLink href={logoHref} analyticsContext={clickContext({ brand: config.name, type: 'logoClick' })}>
						<ImageWrapper
							src={logoUrl}
							width="200px"
							height="unset"
							sx={logoStyles}
							data-test-id="img_logo_desktop_with_nav_menu"
							alt="brand logo"
						/>
					</TAGLink>
				</Box>
				<Box display="flex" justifyContent="flex-end" width={'100%'}>
					<HeaderSearch
						profileLinkUrl={headerData?.profileLinkUrl}
						hideFullSiteSearch={headerData?.hideFullSiteSearch}
						showSchedulingButton
						schedulingButtonUrl={headerData?.schedulingButtonUrl}
						schedulingButtonText={headerData?.schedulingButtonText}
						schedulingNavigationLinksCollection={headerData?.schedulingNavigationLinksCollection}
						locationBarFindOfficeUrl={headerData?.locationBarFindOfficeUrl}
						usePopupScheduling={headerData?.usePopupScheduling}
						isDarkHeader={isDarkHeader}
						hidePrimaryActionButton={hidePrimaryActionButton}
					/>
					{showLanguageToggle && <LanguageToggle isLargeDevice />}
				</Box>
			</Toolbar>
			<Box sx={getNavMenuDesktopStyles} data-test-id="desktop-header-nav-menu">
				<NavigationMenu mainNavItems={navMenuLinks} />
			</Box>
			{showBottomAlertBannerMessages && (
				<Banner
					dataTestId="header_desktop_bottom_alert_banner"
					variant={'Alert Banner'}
					alertMessagesCollection={bottomAlertBannerMessages}
				/>
			)}
		</AppBar>
	) : (
		<>
			<AppBar
				ref={headerElement}
				sx={headerStyles(isDarkHeader, false)}
				data-test-id="section_header_desktop"
				id={DESKTOP_HEADER_ID}
			>
				{showTopAlertBannerMessages && (
					<Banner
						dataTestId="header_desktop_top_alert_banner"
						variant={'Alert Banner'}
						alertMessagesCollection={topAlertBannerMessages}
					/>
				)}
				{topLinks && (
					<NavigationBar
						navigationItems={headerData?.topNavigationLinksCollection}
						navigationSx={topNavigationStyles}
						isUtilityNavigation
						isDarkHeader={isDarkHeader}
					/>
				)}
				<Toolbar disableGutters sx={toolbarHeaderStyles(false)}>
					<Box display="flex" sx={getLogoContainerStyles(logoPosition)}>
						<Box
							display="flex"
							justifyContent="flex-start"
							width={logoPosition === 'left' ? 'fit-content' : '100%'}
						>
							{headerData && !hideLocation && (
								<>
									{useSimpleFindOfficeLink ? (
										<HeaderSimpleFindOfficeLink
											locationBarFindOfficeUrl={headerData?.locationBarFindOfficeUrl ?? ''}
											isDarkHeader={isDarkHeader}
										/>
									) : (
										<HeaderOfficeLocation
											isLargeDevice
											headerData={headerData}
											withOfficeDetails={withOfficeDetails}
											locationNavigationLinks={locationNavigationLinks}
											isDarkHeader={isDarkHeader}
											useHeaderLocationCards={useHeaderLocationCards}
											googleMapsForLocationCards={googleMapsForLocationCards}
										/>
									)}
								</>
							)}
						</Box>

						<Box width="min-content">
							<TAGLink
								href={logoHref}
								analyticsContext={
									config.name === 'ClearChoice'
										? {
												...clearChoiceConsts.context,
												...clearChoiceConsts.logoClick,
										  }
										: ({ clickPosition: 'header' } as AnalyticsContext)
								}
							>
								<ImageWrapper
									src={logoUrl}
									width={variant === 'teamTag' ? '300px' : '200px'}
									height="unset"
									sx={logoStyles}
									data-test-id="img_logo_desktop"
									alt="brand logo"
								/>
							</TAGLink>
						</Box>
					</Box>

					<Box display="flex" sx={inlineNavigationStyles}>
						{inlineNavigation && (
							<NavigationBar
								navigationItems={headerData?.navigationLinksCollection}
								navigationSx={getWrapperStyles(false, logoPosition)}
								isDarkHeader={isDarkHeader}
								showLanguageToggle={showLanguageToggle}
							/>
						)}
						<Box display="flex" justifyContent="flex-end" width={logoPosition === 'left' ? 'none' : '100%'}>
							<HeaderSearch
								profileLinkUrl={headerData?.profileLinkUrl}
								hideFullSiteSearch={headerData?.hideFullSiteSearch}
								showSchedulingButton
								schedulingButtonUrl={headerData?.schedulingButtonUrl}
								schedulingButtonText={headerData?.schedulingButtonText}
								schedulingNavigationLinksCollection={headerData?.schedulingNavigationLinksCollection}
								locationBarFindOfficeUrl={headerData?.locationBarFindOfficeUrl}
								usePopupScheduling={headerData?.usePopupScheduling}
								isDarkHeader={isDarkHeader}
								hidePrimaryActionButton={hidePrimaryActionButton}
							/>
						</Box>
					</Box>
				</Toolbar>
			</AppBar>

			{navigationBar && (
				<AppBar ref={navElement} sx={navbarStyles} data-test-id="section_navbar_desktop">
					<NavigationBar
						navigationItems={headerData?.navigationLinksCollection}
						isDarkHeader={isDarkHeader}
					/>
				</AppBar>
			)}
			{showBottomAlertBannerMessages && (
				<Banner
					dataTestId="header_desktop_bottom_alert_banner"
					variant={'Alert Banner'}
					alertMessagesCollection={bottomAlertBannerMessages}
				/>
			)}
		</>
	);
}
